/**
 * /audit — nhat ky moi thay doi cau hinh (guardrail 7).
 *
 * ============================================================================
 *  VI SAO TRANG NAY DANG TON TAI.
 *
 *  Guardrail 7 bat server ghi mot dong AuditLog cho MOI thao tac quan tri: tao key,
 *  thu hoi key, doi mode profile, kich hoat model, sua upstream, nap dataset. Truoc
 *  trang nay, du lieu do nam trong DB va cach duy nhat de xem la go mot lenh curl.
 *
 *  Mot nhat ky khong ai doc duoc thi khong phai nhat ky — no la mot bang ton dia.
 *  Gia tri cua guardrail 7 chi hien thuc hoa khi co cho de nhin vao no luc su co.
 * ============================================================================
 */

import { useMemo, useState } from 'react';
import type { AuditLogDto } from '@capforge/shared';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, type BadgeTone } from '../components/Badge';
import { ErrorBox } from '../components/ErrorBox';
import { Spinner } from '../components/Spinner';

const LIMITS = [50, 200, 500] as const;

/**
 * Mau theo muc do "kho quay lai" cua hanh dong.
 *
 * Khong to mau theo chu de cho dep: nguoi mo trang nay thuong dang di tim mot thay doi
 * vua lam hong thu gi. Cai ho can thay truoc tien la nhung viec KHO HOAN TAC nhat.
 */
function toneOf(action: string): BadgeTone {
  if (action.includes('delete') || action.includes('revoke') || action.includes('reset')) return 'danger';
  if (action.includes('activate') || action.includes('import') || action.includes('rebuild')) return 'warn';
  if (action.includes('create')) return 'success';
  return 'info';
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export default function Audit(): JSX.Element {
  const [limit, setLimit] = useState<number>(200);
  const [needle, setNeedle] = useState('');
  const [open, setOpen] = useState<number | null>(null);

  const state = useAsync<AuditLogDto[]>((signal) => api.auditLog(limit, signal), [limit]);

  const rows = useMemo(() => {
    const list = state.data ?? [];
    const q = needle.trim().toLowerCase();
    if (q === '') return list;
    return list.filter((r) =>
      `${r.actor} ${r.action} ${r.target ?? ''} ${JSON.stringify(r.detail)}`.toLowerCase().includes(q),
    );
  }, [state.data, needle]);

  if (state.loading) return <Spinner />;
  if (state.error !== null) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Nhật ký quản trị</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Mọi thay đổi cấu hình đều được ghi lại: ai làm, làm gì, lúc nào. Secret luôn bị che
          trước khi ghi — nhật ký này an toàn để chia sẻ khi cần điều tra.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <input
          value={needle}
          onChange={(e) => setNeedle(e.target.value)}
          placeholder="Lọc theo người, hành động, đối tượng…"
          className="cf-input min-w-[16rem] flex-1 py-1.5 text-sm"
        />
        <label className="flex items-center gap-2 text-sm">
          <span className="text-slate-600 dark:text-slate-400">Số dòng</span>
          <select
            className="cf-input py-1"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            {LIMITS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={state.reload}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-600"
        >
          Tải lại
        </button>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {rows.length}/{state.data?.length ?? 0} dòng
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400">
          {needle.trim() === ''
            ? 'Chưa có thao tác quản trị nào được ghi lại.'
            : 'Không có dòng nào khớp bộ lọc.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2">Lúc</th>
                <th className="px-3 py-2">Ai</th>
                <th className="px-3 py-2">Hành động</th>
                <th className="px-3 py-2">Đối tượng</th>
                <th className="px-3 py-2">Chi tiết</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isOpen = open === r.id;
                const detailText = JSON.stringify(r.detail);
                return (
                  <tr
                    key={r.id}
                    className="border-t border-slate-200 align-top dark:border-slate-700"
                  >
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
                      {formatWhen(r.createdAt)}
                    </td>
                    <td className="px-3 py-2 text-xs">{r.actor}</td>
                    <td className="px-3 py-2">
                      <Badge tone={toneOf(r.action)}>{r.action}</Badge>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.target ?? '—'}</td>
                    <td className="px-3 py-2">
                      {detailText === '{}' ? (
                        <span className="text-xs text-slate-400">—</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setOpen(isOpen ? null : r.id)}
                          className="text-left font-mono text-xs text-sky-700 hover:underline dark:text-sky-400"
                        >
                          {isOpen ? (
                            <pre className="whitespace-pre-wrap break-all">
                              {JSON.stringify(r.detail, null, 2)}
                            </pre>
                          ) : (
                            <span>
                              {detailText.length > 80 ? `${detailText.slice(0, 80)}…` : detailText}
                            </span>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
